import type {PageTheme, PageThemeLibraryImage} from "../AccountThemeApi";

/**
 * The sender's theme image LIBRARY as the editor manipulates it (api#471, epic #26 "logic outside React").
 *
 * Sibling of `themeValidation.ts`, which answers "may this be published?"; this one answers "what is in the
 * theme, and in what order?". Kept apart because they change for different reasons: the arity rules follow
 * the treatment catalogue, this follows the sheet's editing model.
 *
 * Before api#471 the api returned only the images the live theme rendered, so the working set could not
 * outlive one open sheet and there was nothing to model: whatever the editor held WAS the theme. Now the
 * read carries every image the sender owns, each saying whether the saved theme uses it, so the editor has
 * two distinct facts per image — is it MINE (always, until deleted) and is it IN THE THEME (a draft the
 * sender edits and Publish commits).
 *
 * Every function here is pure and returns a new array; nothing mutates its input.
 */

/**
 * One image in the editor: the server's record of it plus the DRAFT decision about it.
 *
 * `attached` is the saved theme's answer and only the server changes it. `selected` is the sender's
 * unsaved intent. They agree right after a load and diverge as soon as the sender edits, which is exactly
 * why they are separate fields rather than one — the delete guard has to consult the SAVED one
 * ({@link canDeleteImage}) while the tile shows the draft.
 */
export type ThemeTile = PageThemeLibraryImage & {selected: boolean};

/**
 * The invariant every function here preserves: selected images first, in render order, then the rest.
 *
 * It is the same order the api returns a library in, so a freshly loaded sheet and an edited one are laid
 * out by one rule, and a reload never appears to shuffle anything.
 */
function reorderSelectedFirst(tiles: readonly ThemeTile[]): ThemeTile[] {
	return [...tiles.filter(tile => tile.selected), ...tiles.filter(tile => !tile.selected)];
}

/**
 * The editor's starting state for a loaded theme.
 *
 * With a saved treatment, the draft starts as the saved theme: exactly the attached images, in the order
 * the api listed them. With NO saved treatment every image starts selected instead — an account in that
 * state has images it uploaded and never arranged, and offering them pre-excluded would ask the sender to
 * opt in to their own uploads before they could publish anything at all.
 */
export function tilesOf(theme: PageTheme): ThemeTile[] {
	const unthemed = theme.template === null;
	return theme.images.map(image => ({...image, selected: unthemed || image.attached}));
}

/**
 * Re-projects a server payload onto the draft the sender is already editing — for the response to a WRITE,
 * where {@link tilesOf} would be wrong.
 *
 * The server knows `attached`; it has no idea what is SELECTED, because selection is an unpublished
 * decision that only ever leaves the client as an `assetIds` list. So a write's response must refresh the
 * library and the attachment flags while leaving the draft alone. Deriving selection from `attached` here
 * instead would undo the sender's own edit the instant they published: publishing a Hero from three
 * selected images comes back with one attached, and the other two would silently leave the draft — which
 * is the ui#583 complaint, in a new place.
 *
 * Order follows the DRAFT for images the sender already had, so nothing jumps under them mid-edit; images
 * the payload introduces are appended in the server's order.
 */
export function tilesMerged(theme: PageTheme, previous: readonly ThemeTile[]): ThemeTile[] {
	const draft = new Map(previous.map(tile => [tile.assetId, tile.selected]));
	const merged = theme.images.map(image => ({
		...image,
		selected: draft.get(image.assetId) ?? (theme.template === null || image.attached),
	}));
	const order = new Map(previous.map((tile, at) => [tile.assetId, at]));
	return merged.sort((left, right) =>
		(order.get(left.assetId) ?? merged.length) - (order.get(right.assetId) ?? merged.length));
}

/** The images the theme would carry, in render order. What a save is addressed with. */
export function selectedImages(tiles: readonly ThemeTile[]): ThemeTile[] {
	return tiles.filter(tile => tile.selected);
}

/** Puts an image into the draft theme, at the end of the selected run. */
export function selectImage(tiles: readonly ThemeTile[], assetId: string): ThemeTile[] {
	return reorderSelectedFirst(tiles.map(tile => tile.assetId === assetId ? {...tile, selected: true} : tile));
}

/**
 * Takes an image out of the draft theme WITHOUT deleting it — it keeps its place at the head of the
 * unselected run, so the sender can put it back.
 *
 * This is what the editor's per-tile "remove" did before api#471 as well, except that then the image left
 * the sheet for good and the row was orphaned. Taking an image out of the theme and deleting it are now
 * genuinely different actions, and nothing in this module performs the second one.
 */
export function deselectImage(tiles: readonly ThemeTile[], assetId: string): ThemeTile[] {
	return reorderSelectedFirst(tiles.map(tile => tile.assetId === assetId ? {...tile, selected: false} : tile));
}

/**
 * Whether the sender may delete this image right now.
 *
 * MIRRORS the api's own refusal rather than restating a rule of its own: the api rejects deleting an image
 * that is attached to an ENABLED theme, because that would change every page the sender has ever shared
 * from a settings list. Both sides therefore read the same two server-owned facts — the image's saved
 * `attached` flag and the saved theme's `enabled` — so a disabled control and a 400 can never disagree.
 *
 * Deliberately NOT `selected`: the draft has not been published, so an image the sender has just taken out
 * of the draft is still the one their pages are showing.
 */
export function canDeleteImage(image: PageThemeLibraryImage, savedEnabled: boolean): boolean {
	return !(image.attached && savedEnabled);
}
